import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { setToken as setStoredToken, getToken, CurrentUser } from '../lib/auth';
import { apiLogin, apiMe, apiRegisterOrg, isApiError } from '../lib/api';

export type AuthState = {
  ready: boolean;
  token: string | null;
  user: CurrentUser | null;
  organization: { id: string; name: string; slug: string } | null;
};

export type AuthContextType = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (orgName: string, email: string, password: string, name?: string) => Promise<void>;
  bootstrapSustaintrack: (adminEmail: string, adminPassword: string, adminName?: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ ready: false, token: null, user: null, organization: null });

  // Load token and fetch profile
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setState((s) => ({ ...s, ready: true }));
      return;
    }
    (async () => {
      try {
        const me = await apiMe();
        setState({ ready: true, token, user: me.user, organization: me.organization });
      } catch {
        setStoredToken(null);
        setState({ ready: true, token: null, user: null, organization: null });
      }
    })();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { token } = await apiLogin(email, password);
    setStoredToken(token);
    const me = await apiMe();
    setState({ ready: true, token, user: me.user, organization: me.organization });
  }, []);

  const logout = useCallback(() => {
    setStoredToken(null);
    setState({ ready: true, token: null, user: null, organization: null });
  }, []);

  const register = useCallback(async (orgName: string, email: string, password: string, name?: string) => {
    const { token } = await apiRegisterOrg(orgName, email, password, name);
    setStoredToken(token);
    try {
      const me = await apiMe();
      setState({ ready: true, token, user: me.user, organization: me.organization });
    } catch (err: unknown) {
      // apiMe failed after token was stored — clear to avoid inconsistent auth state
      setStoredToken(null);
      throw err;
    }
  }, []);

  const bootstrapSustaintrack = useCallback(async (adminEmail: string, adminPassword: string, adminName?: string) => {
    let token: string;
    try {
      ({ token } = await apiRegisterOrg('Sustaintrack', adminEmail, adminPassword, adminName));
    } catch (err: unknown) {
      if (isApiError(err) && err.status === 409) {
        // Org already exists from a previous bootstrap — fall back to login
        ({ token } = await apiLogin(adminEmail, adminPassword));
      } else {
        throw err;
      }
    }
    setStoredToken(token);
    const me = await apiMe();
    setState({ ready: true, token, user: me.user, organization: me.organization });
  }, []);

  const value = useMemo<AuthContextType>(() => ({ ...state, login, logout, register, bootstrapSustaintrack }), [state, login, logout, register, bootstrapSustaintrack]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
